import { sectionType } from "../../../sectionType";
import { unitBaseTypeEnum } from "../../../unit-base-type-enum";
import { ColumnBaseService } from "../column-base.service";
import { alignment } from "../models/alignment.enum";
import { UnitAmount } from "../models/column";
import { columnDefs, subtypeEnum, variantEnum } from "./models/column-definition";
import { columnType } from "./models/column-type.enum";
import { crownType } from "./models/crown-type.enum";
import { headType } from "./models/head-type.enum";
import { middleType } from "./models/middle-type.enum";
import { partTypeEnum } from "./models/part-type.enum";
import { plinthType } from "./models/plinth-type.enum";
import { HybridColumn, HybridPart } from "./models/props-config";
import { segmentType } from "./models/segment-type";

export interface Config {
    amountOfGMColumns: number,
    amountOfGS1S2Columns: number,
    amountOfGL1L2Columns: number,
    amountOfGXLColumns: number,
    selectedPlinthType: plinthType,
    selectedMiddleType: middleType,
    selectedCrownType: crownType,
    plinthFloors: number,
    middleFloors: number,
    crownFloors: number,
    selectedMoosPlazaType: columnType,
    selectedMoosPlazaAlignment: alignment,
    headLeftType: headType,
    headRightType: headType,
    nrOfStaircaseCores: number,
    nrOfElevatorCores: number,
    customPlinthHeight: number,
    customPlinthWidth: number,
}
export class ColumnService extends ColumnBaseService {

    getTotalAmountOfAllUnits(parts: HybridPart[]): UnitAmount[] {
        let unitAmounts: UnitAmount[] = [];
        if (parts) {
            unitAmounts = this.computeTotalAmountsForParts(parts);
            const totalGUnits = this.totalGUnits(unitAmounts);
            const totalHUnits = this.totalHUnits(unitAmounts);

            unitAmounts.forEach(unitAmount => {
                if (unitAmount.type.includes('G-')) {
                    unitAmount.percentage = unitAmount.amount / totalGUnits * 100;
                } else if (unitAmount.type.includes('H-')) {
                    unitAmount.percentage = unitAmount.amount / totalHUnits * 100;
                }
            });
        }
        return unitAmounts;
    }

    totalGUnits(units: UnitAmount[]) {
        let gUnits = units.filter(s => s.type.includes('G-'));
        return gUnits.map(u => u.amount).reduce((p, c) => p + c, 0);
    }

    totalHUnits(units: UnitAmount[]) {
        let hUnits = units.filter(s => s.type.includes('H-'));
        return hUnits.map(u => u.amount).reduce((p, c) => p + c, 0);
    }

    createColumnFromDefinition(type: columnType | headType, plinth: plinthType | subtypeEnum, middle: middleType | subtypeEnum, crown: crownType | subtypeEnum, plinthFloors: number, middleFloors: number, crownFloors: number, variant: variantEnum, customPlinthHeight: number, customPlinthWidth: number): HybridColumn {
        const defs = columnDefs;
        const columnDefinition = defs.find(s => s.type === type);
        const bays = columnDefinition.bays;

        let plinthSections: sectionType[] = [sectionType.none];

        let amountOfUnits = [];

        if (plinth !== plinthType.Custom) {
            if ([columnType.O_TH, columnType.O_TLH2, headType._O_TH2, headType._O_TLH2].includes(type)) {
                plinth = subtypeEnum.single;
            }

            const plinthDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.plinth && s.subtype === plinth);
            const plinthFloorDef = plinthDef.floorVariants.find(s => s.floors === plinthFloors);
            const plinthSideDef = plinthFloorDef.variants.find(f => f.variant === variant);
            plinthSections = plinthSideDef.sections;
        }

        let middleSections: sectionType[];

        const middleDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.middle && s.subtype === middle);
        const middleFloorDef = middleDef.floorVariants.find(s => s.floors === 1);


        const middleSideDef = middleFloorDef.variants.find(f => f.variant === variant);
        middleSections = middleSideDef.sections;

        let crownSections: sectionType[];

        const crownDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.crown && s.subtype === crown);
        const crownFloorDef = crownDef.floorVariants.find(s => s.floors === crownFloors);

        const crownVariantDef = crownFloorDef.variants.find(f => f.variant === variant);
        crownSections = crownVariantDef.sections;

        let result: HybridColumn = {
            id: Math.random().toString(),
            type,
            bays,
            segments: [
                {
                    type: segmentType.plinth,
                    subtype: plinth as subtypeEnum,
                    sectionTypes: plinthSections,
                    custom: plinth === plinthType.Custom,
                    floorsInSegment: plinthFloors,
                    customSegmentHeight: (plinth === plinthType.Custom ? customPlinthHeight : 0),
                    customSegmentWidth: (plinth === plinthType.Custom ? customPlinthWidth : 0),
                    variant: variant,
                },
                {
                    type: segmentType.middle,
                    subtype: middle as subtypeEnum,
                    sectionTypes: middleSections,
                    floorsInSegment: middleFloors,
                    variant: variant,
                },
                {
                    type: segmentType.crown,
                    subtype: crown as subtypeEnum,
                    sectionTypes: crownSections,
                    floorsInSegment: crownFloors,
                    variant,
                }
            ],
            amountOfUnits,
        }

        return result;
    }

    createPossibleColumnDefinitions(
        c: Config
    ): HybridPart[] {

        const ch = c.customPlinthHeight;
        const cw = c.customPlinthWidth;

        const headLeftPart = {
            type: partTypeEnum.headLeft,
            columns: [],
        }

        const bodyPart = {
            type: partTypeEnum.body,
            columns: [],
        }


        const headRightPart = {
            type: partTypeEnum.headRight,
            columns: [],
        }

        // Heads

        if (c.headLeftType !== headType.none) {
            headLeftPart.columns.push(
                this.createHeadColumn(
                    alignment.left,
                    c.headLeftType,
                    c.selectedPlinthType,
                    c.selectedMiddleType,
                    c.selectedCrownType,
                    c.plinthFloors,
                    c.middleFloors,
                    c.crownFloors,
                    ch,
                    cw
                )
            )
        }

        // Moos plein
        if (c.selectedMoosPlazaType !== columnType.none) {
            let moospleinColumnVariant = variantEnum.left;
            if (c.selectedMoosPlazaAlignment === alignment.right) {
                moospleinColumnVariant = variantEnum.right;
            }

            if (c.selectedMoosPlazaType === columnType.V_P3) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P3, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                )
            } else if (c.selectedMoosPlazaType === columnType.V_P4_1) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P4_1, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                )
            } else if (c.selectedMoosPlazaType === columnType.V_P4_2) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P4_2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                )
            } else if (c.selectedMoosPlazaType === columnType.V_P5) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P5, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                )
            } else if (c.selectedMoosPlazaType === columnType.V_P4H) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P4H, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                );
            } else if (c.selectedMoosPlazaType === columnType.V_P5H_S1S2) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P5H_S1S2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                )
            } else if (c.selectedMoosPlazaType === columnType.V_P5H_XL) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.V_P5H_XL, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw)
                )
            }
        }

        // extra staircases
        if (c.nrOfStaircaseCores > 0) {
            for (let i = 0; i < c.nrOfStaircaseCores; i++) {

                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.O_TH, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, 1, variantEnum.center, ch, cw)
                );
            }
        }
        if (c.nrOfElevatorCores > 0) {
            for (let i = 0; i < c.nrOfElevatorCores; i++) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.O_TLH2, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, 1, variantEnum.center, ch, cw)
                );
            }
        }

        // Standard
        if (c.amountOfGMColumns > 0) {
            for (let i = 0; i < c.amountOfGMColumns; i++) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.G_M, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.center, ch, cw)
                )
            }
        }
        if (c.amountOfGS1S2Columns > 0) {
            for (let i = 0; i < c.amountOfGS1S2Columns; i++) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.G_S1S2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.center, ch, cw)
                )
            }
        }
        if (c.amountOfGL1L2Columns > 0) {
            for (let i = 0; i < c.amountOfGL1L2Columns; i++) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.G_L1L2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.center, ch, cw)
                )
            }

        }
        if (c.amountOfGXLColumns > 0) {
            for (let i = 0; i < c.amountOfGXLColumns; i++) {
                bodyPart.columns.push(
                    this.createColumnFromDefinition(columnType.G_XL, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.center, ch, cw)
                )
            }
        }

        // Last column is right head type if it exists
        if (c.headRightType !== headType.none) {
            headRightPart.columns.push(
                this.createHeadColumn(alignment.right, c.headRightType, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, ch, cw)
            )
        }

        return [headLeftPart, bodyPart, headRightPart];
    }

    createHeadColumn(
        headAlignment: alignment,
        selectedHeadType: headType,
        selectedPlinthType: plinthType,
        selectedMiddleType: middleType,
        selectedCrownType: crownType,
        plinthFloors: number,
        middleFloors: number,
        crownFloors: number,
        customPlinthHeight: number,
        customPlinthWidth: number
    ) {
        let result: HybridColumn;
        let headVariant = variantEnum.left;
        if (headAlignment === alignment.right) {
            headVariant = variantEnum.right;
        }
        const ch = customPlinthHeight;
        const cw = customPlinthWidth;
        if (selectedHeadType === headType.G_M__O_TH2) {
            result = this.createColumnFromDefinition(headType.G_M__O_TH2, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType.G_M__O_TLH2) {
            result = this.createColumnFromDefinition(headType.G_M__O_TLH2, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType.G_XL__O_TH2) {
            result = this.createColumnFromDefinition(headType.G_XL__O_TH2, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType.G_XL__O_TLH2) {
            result = this.createColumnFromDefinition(headType.G_XL__O_TLH2, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType._2G_MH) {
            result = this.createColumnFromDefinition(headType._2G_MH, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType._2G_LH) {
            result = this.createColumnFromDefinition(headType._2G_LH, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType._2G_M) {
            result = this.createColumnFromDefinition(headType._2G_M, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType._2G_L) {
            result = this.createColumnFromDefinition(headType._2G_L, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)
        } else if (selectedHeadType === headType._O_TH2) {
            result = this.createColumnFromDefinition(headType._O_TH2, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, 1, variantEnum.center, ch, cw)
        } else if (selectedHeadType === headType._O_TLH2) {
            result = this.createColumnFromDefinition(headType._O_TLH2, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, 1, variantEnum.center, ch, cw)
        }
        return result;
    }
}